import { useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  NAV_SECTIONS,
  type NavChild,
  type NavGroup,
  type NavSection,
  type NavVisibilityContext,
} from '@/config/navigation';
import { useAuthStore } from '@/store/auth.store';
import { profileQueryOptions } from '@/features/auth/api/auth.api';
import { USER_TYPE } from '@/features/users/constants/user.constants';

const EXPLICIT_ORIGIN_STRENGTH = Infinity;

function matchStrength(child: NavChild, pathname: string, fromParam: string | null): number {
  if (fromParam === child.href) return EXPLICIT_ORIGIN_STRENGTH;

  const patterns = [child.href, ...(child.activeWhen ?? [])];
  return patterns.reduce((strongest, pattern) => {
    const matches = pathname === pattern || pathname.startsWith(`${pattern}/`);
    return matches ? Math.max(strongest, pattern.length) : strongest;
  }, 0);
}

export interface ActiveNavTrail {
  group: NavGroup;
  child: NavChild;
}

function resolveActiveNavTrail(pathname: string, fromParam: string | null): ActiveNavTrail | null {
  let activeTrail: ActiveNavTrail | null = null;
  let strongest = 0;

  for (const section of NAV_SECTIONS) {
    for (const group of section.groups) {
      for (const child of group.children) {
        const strength = matchStrength(child, pathname, fromParam);
        if (strength > strongest) {
          strongest = strength;
          activeTrail = { group, child };
        }
      }
    }
  }

  return activeTrail;
}

export function useActiveNavTrail(): ActiveNavTrail | null {
  const { pathname, searchStr } = useLocation();
  const fromParam = searchStr ? new URLSearchParams(searchStr).get('from') : null;
  return resolveActiveNavTrail(pathname, fromParam);
}

export function useNavGroupActive(group: NavGroup) {
  const activeHref = useActiveNavTrail()?.child.href ?? null;

  const isChildActive = (child: NavChild) => child.href === activeHref;
  const isActive = group.children.some(isChildActive);
  return { isChildActive, isActive };
}

function labelContains(label: string, query: string): boolean {
  return label.toLowerCase().includes(query);
}

// The current user's details that gate which nav items are visible. The profile
// (for departmentName) is only fetched for a General User, since SA/ADM pass on
// userTypeId alone.
export function useNavVisibilityContext(): NavVisibilityContext {
  const userTypeId = useAuthStore((state) => state.user?.userTypeId ?? null);
  const { data } = useQuery({
    ...profileQueryOptions,
    enabled: userTypeId === USER_TYPE.GENERAL,
  });
  return { userTypeId, departmentName: data?.departmentName ?? null };
}

function isChildVisible(child: NavChild, context: NavVisibilityContext): boolean {
  if (child.isHidden) return false;
  return child.visibleWhen ? child.visibleWhen(context) : true;
}

function removeHiddenNavItems(sections: NavSection[], context: NavVisibilityContext): NavSection[] {
  return sections.flatMap((section) => {
    const groups = section.groups.flatMap((group) => {
      if (group.isHidden) return [];

      const children = group.children.filter((child) => isChildVisible(child, context));
      return children.length > 0 ? [{ ...group, children }] : [];
    });

    return groups.length > 0 ? [{ ...section, groups }] : [];
  });
}

export function filterNavSections(
  sections: NavSection[],
  searchText: string,
  context: NavVisibilityContext,
): NavSection[] {
  const visibleSections = removeHiddenNavItems(sections, context);
  const query = searchText.trim().toLowerCase();
  if (query === '') return visibleSections;

  return visibleSections.flatMap((section) => {
    const groups = section.groups.flatMap((group) => {
      if (labelContains(group.label, query)) return [group];

      const children = group.children.filter((child) => labelContains(child.label, query));
      return children.length > 0 ? [{ ...group, children }] : [];
    });

    return groups.length > 0 ? [{ ...section, groups }] : [];
  });
}
